import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { RootState } from '../../redux/store'
import { useAuth } from '../../hooks/useAuth'
import { useSurveyGuard } from '../../hooks/useSurveyGuard'
import { Button, LoadingSpinner, PageTitle, SkipLink } from '../../components/atoms'
import { setInstitution } from '../../redux/slices/institutionSlice'
import { useDispatch, useSelector } from 'react-redux'
import { fetchCurrentAttempt, getAccount, updateUserProfile } from '../../libs/services'
import { useNavigate } from 'react-router-dom'
import downloadIcon from '../../assets/images/buttons/download-icon.png'
import downloadIconClicked from '../../assets/images/buttons/download-icon-clicked.png'
import { setCurrentAttempt } from '../../redux/slices/quizSlice'
import Text from '../../components/atoms/Text/Text'
import PointBar from '../../components/userAdmin/PointBar'
import ReskinStudentHeader from '../../components/userAdmin/ReskinStudentHeader'
import { useKeyboardControls } from '../../contexts/KeyboardControlsContext'
import { HighlightEffect } from '../../enums/KeyboardControls'
import { ControlHighlight, KeyboardControl } from '../../components/molecules/KeyboardControls'
import { useBackgroundMusic } from '../../contexts/BackgroundMusicContext'
import { getBgmUrl } from '../../utils/audio'
import { BgmTrack } from '../../enums/enums'
import { hasUrlFeatureFlagTrain } from '../../components/molecules/URLFeatureFlag/URLFeatureFlag'
import {
  buildCertificateInputFromFields,
  cleanupCertificatePdfUrl,
  resolveCertificatePdfUrl,
  triggerCertificateDownload
} from '../../utils/certificate'
import CompletedCertificateView, {
  TEXT_CERTIFICATE_SHARING_TRAIN,
  TEXT_CERTIFICATE_SHARING_UNIVERSITY
} from './CompletedCertificateView'

export const idCompletedPageMainContent = 'completed-page-main-content'

export enum FocusableElement {
  ButtonDownload = 'button-download',
  ButtonDone = 'button-done',
}

const Completed: React.FC = () => {
  const { userProfile, refreshUserProfile } = useAuth()
  const { institution } = useSelector((state: RootState) => state.institution)
  const { currentAttempt } = useSelector((state: RootState) => state.quiz)
  const hasFeatureFlagTrainEnabled = hasUrlFeatureFlagTrain()
  const dispatch = useDispatch()
  const navigate = useNavigate()

  // Keyed on the certificate fields rather than the objects holding them, so a new
  // userProfile or attempt reference does not throw away an already generated PDF.
  const firstName = userProfile?.first_name
  const lastName = userProfile?.last_name
  const institutionName = institution?.name
  const endedAt = currentAttempt?.endedAt
  const referenceNumber = currentAttempt?.referenceNumber
  const certificateInput = useMemo(
    () => buildCertificateInputFromFields({ firstName, lastName, institutionName, endedAt, referenceNumber }),
    [firstName, lastName, institutionName, endedAt, referenceNumber]
  )

  // Track if certificate was already downloaded when this page loaded
  // Used to determine if we should auto-redirect to postsurvey
  const [certWasDownloadedOnMount] = useState(
    () => userProfile?.user_profile?.certificate_downloaded === true
  )

  // Skip auto-redirect if cert wasn't downloaded when we loaded the page
  // (user is downloading for first time and should see the Done button)
  const { pendingSurvey, loading: surveyLoading } = useSurveyGuard({
    skip: !certWasDownloadedOnMount
  })

  // If cert was already downloaded when we arrived and postsurvey is pending, redirect
  useEffect(() => {
    if (!surveyLoading && pendingSurvey === 'postsurvey' && certWasDownloadedOnMount) {
      navigate('/postsurvey', { replace: true })
    }
  }, [surveyLoading, pendingSurvey, certWasDownloadedOnMount, navigate])

  // DMVP-1010: In new flow, survey was completed before cert — go straight to dashboard.
  // Uses window.location.replace instead of navigate to force a full page reload,
  // avoiding a React state race where ProtectedRoute reads stale userProfile.
  const onDone = useCallback(async (): Promise<void> => {
    await refreshUserProfile()
    window.location.replace('/user-dashboard')
  }, [refreshUserProfile])

  const bgMusic = useBackgroundMusic()
  const bgmTrack = getBgmUrl(BgmTrack.Completed)

  let textCertificateSharing = TEXT_CERTIFICATE_SHARING_UNIVERSITY
  if (hasFeatureFlagTrainEnabled) {
    textCertificateSharing = TEXT_CERTIFICATE_SHARING_TRAIN
  }

  const getCurrentAttempt = useCallback(async (): Promise<void> => {
    const res = await fetchCurrentAttempt()
    const currentAttempt = res?.data?.data?.attempt
    dispatch(setCurrentAttempt(currentAttempt))
  }, [dispatch])

  // Manage BG music
  useEffect(() => {
    bgMusic.play(bgmTrack)

    return () => {
      bgMusic.pause()
    }
  }, [bgMusic, bgmTrack])

  useEffect(() => {
    if (currentAttempt != null) {
      if (currentAttempt.referenceNumber !== null && currentAttempt?.referenceNumber.length > 0) {
        const fetchInstitution = async (): Promise<void> => {
          if (institution === null) {
            const res = await getAccount()
            dispatch(setInstitution(res.data.account))
          }
        }

        void fetchInstitution()
      } else { // The user hasn't completed the modules - send them back to dashboard:
        navigate('/user-dashboard')
      }
    } else {
      void getCurrentAttempt()
    }
  }, [currentAttempt, dispatch, getCurrentAttempt, institution, navigate, userProfile])

  // Build the PDF up front so the download is instant, but let the download button
  // own the retry: generateCertificatePdf already logs and toasts its own failures.
  useEffect(() => {
    if (certificateInput !== null) {
      const prepareCertificate = async (): Promise<void> => {
        try {
          await resolveCertificatePdfUrl(certificateInput)
        } catch {
          // Reported by the generator; the download button regenerates on click.
        }
      }
      void prepareCertificate()
    }

    return () => {
      cleanupCertificatePdfUrl()
    }
  }, [certificateInput])

  const onDownloadCertificate = useCallback(async (): Promise<boolean> => {
    if (certificateInput === null) {
      return false
    }

    try {
      triggerCertificateDownload(await resolveCertificatePdfUrl(certificateInput))
      return true
    } catch {
      // Reported by the generator.
      return false
    }
  }, [certificateInput])

  // Held on the spinner until the certificate can actually be built, so the student
  // never sees an empty frame above a download button that cannot produce a file.
  if (certificateInput !== null) {
    return (
      <div
        className='font-primary bg-gradient-radial min-h-screen flex flex-col justify-between'
        style={{ // Have to use style directly for tailwind radial gradient:
          backgroundImage: 'radial-gradient(circle, #174E66, #090909)'
        }}
      >
        <SkipLink targetId={idCompletedPageMainContent} />
        <PageTitle title='Certificate of Completion' />
        <header className='overflow-visible'>
          <ReskinStudentHeader>
            <PointBar isCompleted totalExperiencePoints={100} />
          </ReskinStudentHeader>
        </header>
        <main id={idCompletedPageMainContent} tabIndex={-1}>
          <CompletedCertificateView
            hidePointBar
            input={certificateInput}
            sharingText={textCertificateSharing}
          >
            <DownloadButton onDone={onDone} onDownloadCertificate={onDownloadCertificate} />
          </CompletedCertificateView>
        </main>
      </div>
    )
  } else {
    return (
      <div
        className='bg-gradient-radial min-h-screen flex flex-col justify-center items-center'
        style={{ // Have to use style directly for tailwind radial gradient:
          backgroundImage: 'radial-gradient(circle, #174E66, #090909)'
        }}
      ><LoadingSpinner className='w-[25vmin] min-w-[80px] max-w-[240px]' />
      </div>
    )
  }
}

interface DownloadButtonProps {
  onDone: () => Promise<void>
  onDownloadCertificate: () => Promise<boolean>
}

const DownloadButton: React.FC<DownloadButtonProps> = ({ onDone, onDownloadCertificate }) => {
  const [clicked, setClicked] = useState<boolean>(false)
  const keyboardControls = useKeyboardControls()
  const { userProfile, setUserProfile } = useAuth()

  const onDownloadButtonClick = async (): Promise<void> => {
    // Hand the file to the browser first: a student who got no PDF must not be
    // recorded as having downloaded one, and must keep the download button.
    const downloaded = await onDownloadCertificate()
    if (!downloaded) {
      return
    }

    setClicked(true)

    // Update certificate_downloaded in backend
    try {
      await updateUserProfile({ certificate_downloaded: true })
      // Update local state
      if (userProfile !== null) {
        setUserProfile({
          ...userProfile,
          user_profile: {
            ...userProfile.user_profile,
            certificate_downloaded: true
          }
        })
      }
    } catch (err) {
      console.error('Failed to update certificate_downloaded:', err)
    }
  }

  const onDoneButtonClick = (): void => {
    void onDone()
  }

  if (clicked) {
    return (
      <div className='flex flex-row gap-x-8 mt-4'>
        <ControlHighlight
          active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonDownload)}
          highlightEffect={HighlightEffect.ButtonHexOutline}
          data-testid={`highlight-${FocusableElement.ButtonDownload}`}
        >
          <div className='diamond-btn bg-white w-[260px] h-[80px] flex justify-center items-center'>
            <KeyboardControl>
              <Button
                id={FocusableElement.ButtonDownload}
                data-testid={FocusableElement.ButtonDownload}
                bgColor='bg-black'
                className='w-[256px] h-[76px] diamond-btn font-bold'
                type='submit'
                onClick={() => { void onDownloadButtonClick() }}
              >
                <div className='flex flex-row justify-between px-[22px]'>
                  <span className='text-white text-[26px] text-neutral-090'>DOWNLOAD</span>
                  <img src={downloadIconClicked} alt='' aria-hidden='true' className='w-[34.64px]' />
                </div>
              </Button>
            </KeyboardControl>
          </div>
        </ControlHighlight>
        <ControlHighlight
          active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonDone)}
          highlightEffect={HighlightEffect.ButtonHexOutline}
          data-testid={`highlight-${FocusableElement.ButtonDone}`}
        >
          <KeyboardControl>
            <Button
              id={FocusableElement.ButtonDone}
              data-testid={FocusableElement.ButtonDone}
              className='w-[260px] h-[80px] diamond-btn font-bold'
              type='submit'
              onClick={onDoneButtonClick}
            >
              <div className='flex flex-row justify-center px-[22px]'>
                <span className='text-black text-[26px] text-neutral-090'>DONE</span>
              </div>
            </Button>
          </KeyboardControl>
        </ControlHighlight>
      </div>
    )
  }

  return (
    <ControlHighlight
      active={keyboardControls.isEnabled && keyboardControls.isElementFocused(FocusableElement.ButtonDownload)}
      highlightEffect={HighlightEffect.ButtonHexOutline}
      data-testid={`highlight-${FocusableElement.ButtonDownload}`}
      className='mt-4'
    >
      <KeyboardControl>
        <Button
          id={FocusableElement.ButtonDownload}
          data-testid={FocusableElement.ButtonDownload}
          className='w-[260px] h-[80px] diamond-btn font-bold'
          type='submit'
          onClick={() => { void onDownloadButtonClick() }}
        >
          <div className='flex flex-row justify-between items-center px-[22px]'>
            <Text text='DOWNLOAD' />
            <img src={downloadIcon} alt='' aria-hidden='true' className='w-[34.64px]' />
          </div>
        </Button>
      </KeyboardControl>
    </ControlHighlight>
  )
}

export default Completed
